/**
 * LibrariesPage — /agents/libraries
 * The hub of reusable runtime inputs on the DashboardPage template. Demo page: /file-manager and /ui-elements/cards (a
 * grid of cards, one per library). Catalog review counts need the superadmin policy the catalog routes require, so
 * the review tile and its queries appear only for that policy.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bot,
  BookOpenCheck,
  BrainCircuit,
  ClipboardCheck,
  LibraryBig,
  SlidersHorizontal,
  Sparkles,
  Workflow,
} from 'lucide-react';
import {
  listCatalogCandidatesOptions,
  listCatalogItemVersionsOptions,
  listCatalogReviewQueueOptions,
  listCatalogSourcesOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Callout } from '../../components/ui/index.js';
import { DashboardGrid, DashboardPage } from '../../components/page/index.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { tileState } from './libraryReviewHelpers.js';
import { LibraryTileCard, type LibraryTile } from './libraries/LibraryTileCard.js';

export const USABLE_LIBRARIES: readonly LibraryTile[] = [
  {
    title: 'Models / Model Capabilities',
    description: 'Provider model catalog, capability flags, context limits and daemon diagnostics.',
    path: '/agents/libraries/models',
    icon: BrainCircuit,
    meta: 'Read-only capability library',
    targetTypes: ['model', 'model_capability'],
  },
  {
    title: 'Model Runtime Policies',
    description: 'Reusable runtime templates for model calls, tool loops, retries and provider permissions.',
    path: '/agents/libraries/model-runtime-policies',
    icon: SlidersHorizontal,
    meta: 'Dynamic runtime policy templates',
    targetTypes: ['model_runtime_policy'],
  },
  {
    title: 'Agent Templates',
    description: 'Reusable agent profiles and runtime instructions used by workflows and workforce.',
    path: '/agents/templates',
    icon: Bot,
    meta: 'Existing template library',
    targetTypes: ['agent_template'],
  },
  {
    title: 'Skills',
    description: 'Procedural memory that agents load for repeatable work, protocol rules and review gates.',
    path: '/agents/skills',
    icon: BookOpenCheck,
    meta: 'Agent procedural memory',
    targetTypes: ['skill'],
  },
  {
    title: 'Tools',
    description: 'Executable tools from built-in, MCP, provider-managed and internal sources.',
    path: '/agents/tools',
    icon: Sparkles,
    meta: 'Tool catalog',
    targetTypes: ['tool'],
  },
  {
    title: 'Workflows',
    description: 'Reusable workflow definitions that orchestrate tools, templates and gates.',
    path: '/agents/workflows',
    icon: Workflow,
    meta: 'Runtime orchestration',
    targetTypes: ['workflow'],
  },
  {
    title: 'Evaluations',
    description: 'Datasets and checks used to judge templates, tools, workflows and promotions.',
    path: '/admin/evaluations',
    icon: ClipboardCheck,
    meta: 'Governance quality library',
    targetTypes: ['evaluation'],
  },
];

export const DISCOVERY_OUTCOMES: readonly LibraryTile[] = [
  {
    title: 'Catalog Review',
    description: 'Review daemon-discovered candidates, blocked review jobs, source provenance and promoted versions.',
    path: '/agents/libraries/catalog-review',
    icon: LibraryBig,
    meta: 'Review and promotion workspace',
  },
];

const SUMMARY_QUERY = { query: { limit: '500' } } as const;

/** The counts behind the tiles. Only a superadmin may read the catalog, so the queries stay off otherwise. */
function useCatalogSummary(enabled: boolean) {
  const candidates = useQuery({ ...listCatalogCandidatesOptions(SUMMARY_QUERY), enabled });
  const queue = useQuery({ ...listCatalogReviewQueueOptions(SUMMARY_QUERY), enabled });
  const versions = useQuery({ ...listCatalogItemVersionsOptions(SUMMARY_QUERY), enabled });
  const sources = useQuery({ ...listCatalogSourcesOptions(SUMMARY_QUERY), enabled });
  return {
    summary: {
      candidates: candidates.data?.data ?? [],
      queueItems: queue.data?.data ?? [],
      versions: versions.data?.data ?? [],
      sources: sources.data?.data ?? [],
      loading: enabled && (candidates.isPending || queue.isPending || versions.isPending || sources.isPending),
    },
    error: candidates.isError || queue.isError || versions.isError || sources.isError,
  };
}

export const LibrariesPage: React.FC = () => {
  const canReview = usePolicy('RequireSuperadmin');
  const { summary, error } = useCatalogSummary(canReview);
  const tiles = canReview ? [...USABLE_LIBRARIES, ...DISCOVERY_OUTCOMES] : USABLE_LIBRARIES;

  return (
    <DashboardPage
      title="Libraries"
      subtitle="Reusable runtime inputs for AI agents and workflows."
      breadcrumbs={[{ label: 'AI agents' }, { label: 'Libraries' }]}
      alert={
        error ? (
          <Callout tone="danger" title="Could not load catalog review counts">
            The numbers on the cards below may be incomplete.
          </Callout>
        ) : undefined
      }
    >
      <DashboardGrid>
        {tiles.map((item) => (
          <div key={item.title} className="xl:col-span-4">
            <LibraryTileCard item={item} state={canReview ? tileState(item, summary) : { stats: [] }} />
          </div>
        ))}
      </DashboardGrid>
    </DashboardPage>
  );
};
